import { FC, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppBar, Box, Toolbar, IconButton, ButtonBase } from '@mui/material';
import { experimentalStyled } from '@mui/material/styles';
import type { AppBarProps } from '@mui/material';
import MenuIcon from '../../icons/Menu';
import AccountPopover from './utilityMenu/AccountPopover';
import VendorItem from './primaryMenu/VendorItem';
import '../../theme/partials/GlobalNav.css';
import useVendor from 'src/hooks/useVendor';
import { navItems } from 'src/routes';
import useAuth from 'src/hooks/useAuth';
import VendorApi from 'src/api/VendorApi';

interface GlobalHeaderNavbarProps extends AppBarProps {
  onSidebarMobileOpen?: () => void;
}

const GlobalHeaderNavbarRoot = experimentalStyled(AppBar)(({ theme }) => ({
  ...(theme.palette.mode === 'light' && {
    backgroundColor: theme.palette.primary.main,
    boxShadow: 'none',
    color: theme.palette.primary.contrastText,
  }),
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  }),
  zIndex: theme.zIndex.drawer + 100,
}));

const GlobalHeaderNavbar: FC<React.PropsWithChildren<GlobalHeaderNavbarProps>> = (props) => {
  const { onSidebarMobileOpen, ...other } = props;
  const { vendor } = useVendor();
  const location = useLocation();
  const [activeNavItem, setActiveNavItem] = useState<string | null>(null);
  const { user, hasImpersonateVendorAuth, hasBuyerOrCategoryManagerVendorPortalRole } = useAuth();
  const [showPrinting, setShowPrinting] = useState<boolean>(false);
  const baseUrl = process.env.REACT_APP_SERVICE_URL;

  useEffect(() => {
    const urlSegment = location.pathname.split('/').pop();
    setActiveNavItem(urlSegment);
  }, [location]);

  useEffect(() => {
    const fetchPrograms = async () => {
      let hasPrograms: boolean = vendor?.number ? await VendorApi.getDoesVendorHavePrograms(vendor.number) : false;
      if (hasPrograms) {
        setShowPrinting(true);
      } else {
        setShowPrinting(false);
      }
    };

    fetchPrograms();
  }, [vendor?.number]);

  return (
    <GlobalHeaderNavbarRoot {...other} className="global-nav">
      <Toolbar>
        {/* Left Side */}
        <IconButton className="global-sidebar__toggle" onClick={onSidebarMobileOpen}>
          <MenuIcon fontSize="small" />
        </IconButton>

        <RouterLink to="/" className="vendorportal-header-logo">
          <img alt="logo" src="/static/bfg_marketplace_expo_logo.svg" width="90px" />
        </RouterLink>

        <Box className="global-header__menu" id="global-header__menu__item__vendor-select">
          <Box className="global-header__menu__item">
            <VendorItem isPopover />
          </Box>
        </Box>

        {vendor &&
          vendor.numberString &&
          navItems.map((item, index) => {
            //user must be an admin to see the Users menu item
            if (!hasImpersonateVendorAuth() && item.name === 'Users' && user.vendorPortalRole !== 'Admin') {
              return null;
            }

            return (
              <Box
                key={index}
                id={`global-nav__item-link__${item.path.split('/')[1]}`}
                className={`global-header__menu__item ${(item.name === 'Dashboard' && !isNaN(Number(activeNavItem))) || item.path.split('/')[1] === activeNavItem ? 'active' : ''}`}
              >
                <ButtonBase disableRipple>
                  <div className="global-nav__item-link">
                    <RouterLink to={`/${vendor.numberString + item.path}`}>{item.name}</RouterLink>
                  </div>
                </ButtonBase>
              </Box>
            );
          })}

        {/* Bulk Upload */}
        {hasImpersonateVendorAuth() && (
          <Box key={'12243'} id="global-nav__item-link__bulk_upload" className={'global-header__menu__item'}>
            <ButtonBase disableRipple>
              <div className="global-nav__item-link">
                <RouterLink to={'/BulkUserUpload'}>Bulk User Upload</RouterLink>
              </div>
            </ButtonBase>
          </Box>
        )}

        {hasBuyerOrCategoryManagerVendorPortalRole() && (
          <Box
            key={'vendor-settings-nav'}
            id="global-nav__item-link__vendor_settings"
            className={`global-header__menu__item ${activeNavItem === 'VendorSettings' ? 'active' : ''}`}
          >
            <ButtonBase disableRipple>
              <div className="global-nav__item-link">
                <RouterLink to={'/VendorSettings'}>Vendor Settings</RouterLink>
              </div>
            </ButtonBase>
          </Box>
        )}

        {showPrinting && (
          <Box key={'12244'} id="global-nav__item-link__bulk_upload" className={'global-header__menu__item'}>
            <ButtonBase disableRipple>
              <div className="global-nav__item-link">
                <a href={`${baseUrl}/Reports/GetProductLabels?vendorNumber=${vendor.numberString}`} target="_blank" rel="noopener noreferrer">
                  Product Labels
                </a>
              </div>
            </ButtonBase>
          </Box>
        )}

        {showPrinting && (
          <Box key={'12245'} id="global-nav__item-link__bulk_upload" className={'global-header__menu__item'}>
            <ButtonBase disableRipple>
              <div className="global-nav__item-link">
                <a href={`${baseUrl}/Reports/GetShowBook?vendorNumber=${vendor.numberString}`} target="_blank" rel="noopener noreferrer">
                  Show Book
                </a>
              </div>
            </ButtonBase>
          </Box>
        )}

        {/* Spacer */}
        <Box
          sx={{
            flexGrow: 1,
            ml: 2,
          }}
        />
        {/* Spacer */}
        {/* Right Side */}
        <Box sx={{ ml: 2 }}>
          <AccountPopover />
        </Box>
      </Toolbar>
    </GlobalHeaderNavbarRoot>
  );
};

GlobalHeaderNavbar.propTypes = {
  onSidebarMobileOpen: PropTypes.func,
};

export default GlobalHeaderNavbar;
